import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { NumericInput } from '@client/components/_ui/data-entry/NumericInput';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction } from '@client/components/_ui/feedback/modal';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { trpc } from '@client/trpc/client';
import { buildNumericInputRules } from '@client/utils/validation/numeric';
import {
    CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE,
    type ClaimClientUpdateConfiguration,
    DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION,
} from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Divider, Form, Input, Row, Select, Switch } from 'antd';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/handling-standards')({
    staticData: {
        metadata: {
            title: 'Customer updates',
        },
        ui: {
            title: 'Claims configuration',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimHandlingStandards />
        </PageController>
    );
}

const FormItem = Form.Item<ClaimClientUpdateConfiguration>;

function ClaimHandlingStandards() {
    const upsertConfigurationItemOrgMutation = trpc.configurationItem.upsertConfigurationItemOrg.useMutation();
    const deleteConfigurationItemOrgMutation = trpc.configurationItem.deleteConfigurationItemOrg.useMutation();

    const [form] = Form.useForm<ClaimClientUpdateConfiguration>();
    const isEnabled = Form.useWatch(({ isEnabled }) => isEnabled, form);

    const { isLoading, refetch } = useConfigServiceLoader(async (configService) => {
        const configuration = await configService.fetchClaimClientUpdateConfiguration({
            staleTime: 'immediate',
        });

        form.setFieldsValue({
            ...configuration,
            // Configs saved before this field existed won't include it — show the default.
            headerWidgetLabel:
                configuration.headerWidgetLabel || DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION.headerWidgetLabel,
        });
    });

    const isSaving = upsertConfigurationItemOrgMutation.isPending || deleteConfigurationItemOrgMutation.isPending;

    return (
        <Row gutter={16}>
            <Col span={12}>
                <Card>
                    {isLoading ? (
                        <SkeletonBlock variant="form" rows={8} />
                    ) : (
                        <Form
                            form={form}
                            layout="vertical"
                            disabled={!isEnabled}
                            onFinish={(values) => {
                                message.loading('Saving...');

                                upsertConfigurationItemOrgMutation.mutate(
                                    {
                                        name: 'CLAIM_CLIENT_UPDATE_CONFIGURATION',
                                        value: JSON.stringify({
                                            ...values,
                                            headerWidgetLabel: values.headerWidgetLabel?.trim(),
                                        }),
                                    },
                                    {
                                        onSuccess() {
                                            message.success('Saved.');
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            }}
                        >
                            <FormItem
                                label="Is claim client update enabled?"
                                name="isEnabled"
                                tooltip="When enabled, the system will track claim client update intervals and send reminder notifications"
                            >
                                <Switch disabled={false} />
                            </FormItem>

                            <Divider titlePlacement="start" className="text-sm text-subtle-foreground">
                                Claim header
                            </Divider>

                            <FormItem
                                label="Header widget name"
                                name="headerWidgetLabel"
                                rules={[
                                    { required: true, whitespace: true, message: 'Header widget name is required' },
                                    { max: 40, message: 'Header widget name must be 40 characters or fewer' },
                                ]}
                                tooltip="Label shown for the client update widget on the claim header and the claims list. For example, Australian teams may prefer 'GICOP update'."
                            >
                                <Input />
                            </FormItem>

                            <Divider titlePlacement="start" className="text-sm text-subtle-foreground">
                                Timing
                            </Divider>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <FormItem
                                        label="Update interval"
                                        name="intervalDays"
                                        rules={buildNumericInputRules({
                                            valueType: 'number',
                                            isRequired: true,
                                            min: 1,
                                            max: 365,
                                        })}
                                        tooltip="Number of days between required client updates"
                                    >
                                        <NumericInput
                                            valueType="number"
                                            precision={0}
                                            className="w-full"
                                            addonAfter="days"
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={8}>
                                    <FormItem
                                        label="Due soon reminder"
                                        name="dueSoonDays"
                                        rules={buildNumericInputRules({
                                            valueType: 'number',
                                            isRequired: true,
                                            min: 1,
                                            max: 30,
                                        })}
                                        tooltip="Send a reminder this many days before the update is due"
                                    >
                                        <NumericInput
                                            valueType="number"
                                            precision={0}
                                            className="w-full"
                                            addonAfter="days"
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={8}>
                                    <FormItem
                                        label="Day type"
                                        name="dayIntervalType"
                                        rules={[{ required: true }]}
                                        tooltip="Business days exclude weekends"
                                    >
                                        <Select
                                            options={[
                                                {
                                                    value: CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE.BUSINESS,
                                                    label: 'Business days',
                                                },
                                                {
                                                    value: CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE.CALENDAR,
                                                    label: 'Calendar days',
                                                },
                                            ]}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>

                            <Divider titlePlacement="start" className="text-sm text-subtle-foreground">
                                Notifications
                            </Divider>

                            <FormItem
                                label="Additional notification email"
                                name="notificationEmail"
                                rules={[{ type: 'email' }]}
                                tooltip="Optionally send notifications to this email address in addition to the claim owner."
                            >
                                <Input type="email" />
                            </FormItem>

                            <Divider titlePlacement="start" className="mb-2 text-sm text-subtle-foreground">
                                Task settings
                            </Divider>

                            <p className="mb-4 text-sm text-subtle-foreground">
                                Configure the task that is automatically created for each claim.
                            </p>
                            <FormItem label="Title" name={['task', 'title']} rules={[{ required: true }]}>
                                <Input />
                            </FormItem>
                            <FormItem label="Description" name={['task', 'description']} rules={[{ required: true }]}>
                                <Input.TextArea rows={2} />
                            </FormItem>

                            <Divider titlePlacement="start" className="mb-2 text-sm text-subtle-foreground">
                                Milestone settings
                            </Divider>

                            <p className="mb-4 text-sm text-subtle-foreground">
                                Configure the milestone that tracks client update progress on the claim timeline.
                            </p>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <FormItem
                                        label="Label"
                                        name={['milestone', 'labelWhenCompleted']}
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </FormItem>
                                </Col>
                            </Row>

                            <div className="mt-4 flex justify-end gap-2">
                                <Tooltip title="Reset to default settings">
                                    <Button
                                        type="secondary"
                                        loading={isSaving}
                                        onClick={() =>
                                            confirmAction({
                                                title: 'Reset?',
                                                content: 'Do you want to reset these fields to their original values?',
                                                onOk() {
                                                    message.loading('Resetting...');

                                                    deleteConfigurationItemOrgMutation.mutate(
                                                        { name: 'CLAIM_CLIENT_UPDATE_CONFIGURATION' },
                                                        {
                                                            onSuccess() {
                                                                message.success(
                                                                    'Configuration was reset to default values.',
                                                                );
                                                                void refetch();
                                                            },
                                                            onError(error) {
                                                                errorMessage.show(error);
                                                            },
                                                        },
                                                    );
                                                },
                                            })
                                        }
                                    >
                                        Reset
                                    </Button>
                                </Tooltip>
                                <Button type="primary" htmlType="submit" loading={isSaving}>
                                    Save
                                </Button>
                            </div>
                        </Form>
                    )}
                </Card>
            </Col>
        </Row>
    );
}
